import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { varFade, MotionViewport } from 'src/components/animate';

// ----------------------------------------------------------------------

const SERVICES = [
  {
    icon: '/assets/icons/home/ic_react.svg',
    title: 'Product-Scale Frontend Engineering',
    description:
      'Building scalable, high-performance frontend applications with a strong emphasis on state management, maintainability, and long-term product growth.',
  },
  {
    icon: '/assets/icons/home/ic_next.svg',
    title: 'Next.js Architecture & Rendering',
    description:
      'Designing SEO-friendly applications with Next.js, focusing on SSR, SSG, routing strategies, API integration, and performance optimization.',
  },
  {
    icon: '/assets/icons/home/ic_ai.png',
    title: 'Conversational AI Integrations',
    description:
      'Integrating enterprise-grade conversational AI platforms to deliver intelligent, data-driven customer interactions with analytics and NLP insights.',
  },
  {
    icon: '/assets/icons/home/ic_interface.svg',
    title: 'Design Systems & Component Libraries',
    description:
      'Creating scalable design systems and reusable component libraries with a focus on accessibility, consistency, and cross-team collaboration.',
  },
  {
    icon: '/assets/icons/home/ic_perf.png',
    title: 'Performance & Web Vitals',
    description:
      'Optimizing application performance through modern rendering techniques, code-splitting, and Web Vitals improvements.',
  },
  {
    icon: '/assets/icons/home/ic_migration.png',
    title: 'Legacy Modernization',
    description:
      'Modernizing legacy frontend applications by migrating to React-based architectures with improved performance, stability, and developer experience.',
  },
  {
    icon: '/assets/icons/home/ic_integration.png',
    title: 'Frontend Infrastructure & Integrations',
    description:
      'Building reliable frontend infrastructure and integrating third-party services, payment systems, maps, and headless CMS platforms.',
  },
  {
    icon: '/assets/icons/home/ic_devops.png',
    title: 'Frontend DevOps & Delivery',
    description:
      'Establishing CI/CD pipelines, deployment workflows, and environment strategies to ensure fast, reliable, and scalable frontend delivery.',
  },
  {
    icon: '/assets/icons/home/ic_code.png',
    title: 'Code Quality & Mentorship',
    description:
      'Driving engineering excellence through code reviews, best practices, and mentoring developers to build high-quality, maintainable software.',
  },
];

// ----------------------------------------------------------------------

export default function HomeCapabilities() {
  return (
    <Container
      component={MotionViewport}
      sx={{
        py: { xs: 10, md: 15 },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          textAlign: 'center',
          mb: { xs: 5, md: 10 },
        }}
      >
        <m.div variants={varFade().inUp}>
          <Typography component="div" variant="overline" sx={{ color: 'text.disabled' }}>
            Technical Capabilities
          </Typography>
        </m.div>

        <m.div variants={varFade().inDown}>
          <Typography sx={{ mb: 5 }} variant="h2">
            What I Build & Lead
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              textAlign: 'justify',
              maxWidth: 860,
              mx: 'auto',
            }}
          >
            I design and build scalable frontend systems that balance performance, maintainability,
            and user experience. With experience across startups and enterprise products, I focus on
            architecture-level decisions, clean component design, and engineering best practices.
            Beyond coding, I actively share knowledge, mentor developers, and create content around
            modern frontend engineering and product thinking.
          </Typography>
        </m.div>
      </Stack>

      <Box
        gap={{ xs: 3, lg: 10 }}
        display="grid"
        alignItems="center"
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          md: 'repeat(3, 1fr)',
        }}
      >
        {SERVICES.map((card) => (
          <m.div variants={varFade().inUp} key={card.title}>
            <Card
              tabIndex={0}
              sx={{
                position: 'relative',
                borderRadius: 1.5,
                bgcolor: 'background.default',
                p: (theme) => theme.spacing(5, 5),

                // Base shadow
                boxShadow: (theme) => ({
                  xs: theme.customShadows.z8,
                  md: `-40px 40px 80px ${
                    theme.palette.mode === 'light'
                      ? alpha(theme.palette.grey[500], 0.16)
                      : alpha(theme.palette.common.black, 0.4)
                  }`,
                }),

                border: '1px solid',
                borderColor: 'divider',

                transition: 'all 0.35s ease',

                // Hover (desktop)
                '&:hover': {
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  transform: 'translateY(-6px)',
                  boxShadow: (theme) => theme.customShadows.z24,
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                },

                // Keyboard focus (accessibility)
                '&:focus-visible': {
                  outline: 'none',
                  borderColor: (theme) => theme.palette.primary.main,
                },

                // Disable hover lift on touch devices
                '@media (hover: none)': {
                  '&:hover': {
                    transform: 'none',
                    boxShadow: (theme) => theme.customShadows.z8,
                  },
                },
              }}
            >
              <Stack
                flexGrow={1}
                justifyContent="center"
                alignItems={{ xs: 'center', md: 'flex-start' }}
                sx={{
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <Box
                  component="img"
                  src={card.icon}
                  alt={card.title}
                  sx={{
                    width: 48,
                    height: 48,
                    transition: 'transform 0.3s ease',
                    '.MuiCard-root:hover &': {
                      transform: 'scale(1.05)',
                    },
                  }}
                />

                <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
                  {card.title}
                </Typography>

                <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                  {card.description}
                </Typography>
              </Stack>
            </Card>
          </m.div>
        ))}
      </Box>
    </Container>
  );
}
